import React, { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { toast } from "sonner";
import { Icon } from "../../";
import { useApiKeys, useCreateApiKey } from "../../../../hooks/useApiKeys";

/**
 * Special component for the Ordering API Key config field
 * Automatically fetches user's API key or creates one if none exists
 * Then saves it to the config
 */
const ApiKeyConfigField = ({ config }) => {
  const queryClient = useQueryClient();
  const hasInitialized = useRef(false);

  const { data: apiKeys, isLoading: isLoadingKeys } = useApiKeys();
  const createApiKeyMutation = useCreateApiKey();

  const updateConfigMutation = useMutation({
    mutationFn: async ({ configId, key, value }) => {
      const response = await apiClient.put(`/configs/${configId}`, {
        key,
        value,
      });
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pluginConfigCategory"] });
    },
  });

  const existingApiKey = apiKeys?.[0]?.key || null;
  const currentConfigValue = config.value || "";

  const isConfigValueValid =
    currentConfigValue &&
    apiKeys?.some((key) => key.key === currentConfigValue);

  useEffect(() => {
    if (hasInitialized.current) return;
    if (isLoadingKeys) return;

    const setupApiKey = async () => {
      hasInitialized.current = true;

      if (isConfigValueValid) {
        return;
      }

      const needsNewApiKey = !currentConfigValue || !isConfigValueValid;

      if (!needsNewApiKey) {
        return;
      }

      try {
        let apiKeyToUse = existingApiKey;

        if (!apiKeyToUse) {
          toast.info("Creating API key...");
          const newKey = await createApiKeyMutation.mutateAsync();
          apiKeyToUse = newKey?.key;
          toast.success("API key created");
        }

        if (apiKeyToUse) {
          await updateConfigMutation.mutateAsync({
            configId: config.id,
            key: config.key,
            value: apiKeyToUse,
          });
          toast.success("API key configured automatically");
        }
      } catch (error) {
        toast.error(error.message || "Failed to setup API key");
      }
    };

    setupApiKey();
  }, [
    isLoadingKeys,
    existingApiKey,
    currentConfigValue,
    isConfigValueValid,
    config.id,
    config.key,
  ]);

  const isLoading =
    isLoadingKeys ||
    createApiKeyMutation.isPending ||
    updateConfigMutation.isPending;
  const displayValue = isConfigValueValid
    ? currentConfigValue
    : existingApiKey || "";

  const handleCopyApiKey = async () => {
    if (!displayValue) return;
    try {
      await navigator.clipboard.writeText(displayValue);
      toast.success("API key copied to clipboard");
    } catch {
      toast.error("Failed to copy API key");
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-1 flex items-center space-x-2">
            <h4 className="text-sm font-medium text-white">{config.name}</h4>
            {isLoading && (
              <Icon
                name="Loader"
                className="h-4 w-4 animate-spin text-blue-400"
              />
            )}
          </div>
          {config.description && (
            <p className="mb-1 text-xs text-gray-400">{config.description}</p>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        <div className="flex w-full items-center justify-between">
          {isLoading || isLoadingKeys || displayValue === "" ? (
            <div className="flex items-center space-x-2">
              <Icon
                name="Loader"
                className="h-4 w-4 animate-spin text-blue-400"
              />
              <span className="text-sm text-gray-400">
                {isLoadingKeys
                  ? "Loading API key..."
                  : "Configuring API key..."}
              </span>
            </div>
          ) : displayValue ? (
            <div className="flex w-full items-center space-x-2">
              <code
                className={`flex-1 truncate rounded-md border bg-gray-900 px-3 py-2 font-mono text-sm ${
                  isConfigValueValid
                    ? "border-gray-600 text-green-400"
                    : "border-yellow-600 text-yellow-400"
                }`}
              >
                {displayValue}
              </code>
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleCopyApiKey}
                  className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
                  title="Copy API key"
                >
                  <Icon name="Copy" className="h-4 w-4" />
                </button>
                {isConfigValueValid ? (
                  <span className="inline-flex items-center rounded border border-green-500/30 bg-green-900/20 px-2 py-1 text-xs font-medium text-green-400">
                    <Icon name="Check" className="mr-1 h-3 w-3" />
                    Configured
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded border border-yellow-500/30 bg-yellow-900/20 px-2 py-1 text-xs font-medium text-yellow-400">
                    <Icon name="AlertCircle" className="mr-1 h-3 w-3" />
                    Updating...
                  </span>
                )}
              </div>
            </div>
          ) : (
            <span className="text-sm text-gray-400">No API key available</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiKeyConfigField;
